import React, { useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import classes from '../../Schreibtisch.module.css';
import {
  caseDetailsMapper,
  CaseDetailsModel,
  extractAttachmentRequestData,
  FachdatenNode
} from '../../../../api/models/Case/CaseDetailsModel';
import Metadaten from './Metadaten/Metadaten';
import VermerkData from './Vermerk/VermerkData';
import { FachdatenV2 } from './FachdatenV2/FachdatenV2';
import {
  Alert,
  Accordion,
  Button,
  DefaultPageSkeleton,
  DescriptionList,
  Divider,
  Icon,
  Modal,
  Skeleton,
  Spacer,
  Spinner,
  Stack,
  Switch,
  Text,
  Card
} from '@efast_public/fjd-component-library';
import Ausweisdaten from './Ausweisdaten/Ausweisdaten';
import { useTranslation } from 'react-i18next';
import {
  CaseForwardingAttemptEventDto,
  ForwardingAttemptEvent,
  CaseWeiterleitungAttemptPost
} from '../../../../api/models/Forwarding/Forwarding';
import { TFunction } from 'i18next';
import _moment from 'moment/moment';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BehApiRoutes, behClient } from '../../../../api/beh-api';
import { useParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import { useNotification } from '../../../../shared/notification/useNotification';
import { AntragPrintV2 } from '../../../../shared/antragPrint/AntragPrintV2';

const hasSuccessfulAttempt = (attempts: Array<ForwardingAttemptEvent>) => {
  return !!attempts.find((attempt) => attempt.status === 'SUCCESS');
};
const isPendingAttempt = (attempts: Array<ForwardingAttemptEvent>) => {
  return attempts.length === 1 && attempts[0].status === 'SCHEDULED';
};
function AttemptAlert({
  t,
  destination
}: {
  readonly t: TFunction;
  readonly destination: CaseForwardingAttemptEventDto;
}) {
  if (hasSuccessfulAttempt(destination.attemptEvents)) {
    return (
      <Alert
        variant="success"
        message={t('antragsdetails.weiterleitung.status.success', {
          timestamp: _moment
            .utc(destination.attemptEvents[0].occuredAt)
            .local()
            .format('DD.MM.YYYY [um] HH:mm')
        })}
      />
    );
  }
  if (isPendingAttempt(destination.attemptEvents)) {
    return (
      <Alert
        variant="info"
        message={t('antragsdetails.weiterleitung.status.pending')}
      />
    );
  }

  return (
    <Alert
      message={
        <div>
          {t('antragsdetails.weiterleitung.status.error')}
          <br />
          {destination.attemptEvents.map((attempt, index) => (
            <div key={`${attempt.occuredAt}-${attempt.attemptedBy}`}>
              {t(
                `antragsdetails.weiterleitung.attempt.${attempt.attemptedBy}`,
                {
                  attempt: index + 1,
                  timestamp: _moment
                    .utc(attempt.occuredAt)
                    .local()
                    .format('DD.MM.YYYY [um] HH:mm')
                }
              )}
            </div>
          ))}
        </div>
      }
      variant="warning"
    />
  );
}

interface AntragdetailsSettings {
  hideEmpty: boolean;
  showApplicantLabel: boolean;
}

const Antragsdetails = () => {
  const { t } = useTranslation();
  const { notifyUserFromRequest } = useNotification();
  const queryClient = useQueryClient();
  const { caseId } = useParams();
  const {
    data: caseDetails,
    refetch: refetchCaseDetails,
    isLoading: isLoadingCaseDetails
  } = useQuery<CaseDetailsModel, Error>({
    queryKey: [BehApiRoutes.case(caseId ?? '')]
  });

  const [isDisplaySettingsModalOpen, setIsDisplaySettingsModalOpen] =
    useState(false);
  const [antragdetailsSettings, setAntragdetailsSettings] =
    useState<AntragdetailsSettings>({
      hideEmpty: false,
      showApplicantLabel: false
    });

  const componentRef = useRef<HTMLDivElement | null>(null);

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `Antrag_${caseDetails?.caseId || 'details'}`
  });

  const {
    data: caseAttempts,
    refetch: fetchCaseForwarding,
    isLoading
  } = useQuery<Array<CaseForwardingAttemptEventDto>, AxiosError>({
    queryKey: [
      BehApiRoutes.caseWeiterleitung(
        caseId ?? '',
        caseDetails?.initialSubmission?.submissionId ?? ''
      )
    ],
    enabled: !!caseId && !!caseDetails?.initialSubmission?.submissionId
  });

  const { mutateAsync: triggerNewAttempt, isPending: isTriggeringNewAttempt } =
    useMutation({
      mutationFn: async (data: CaseWeiterleitungAttemptPost) =>
        notifyUserFromRequest(
          () =>
            behClient.post(
              BehApiRoutes.caseWeiterleitungAttempt(
                data.caseId,
                data.submissionId
              ),
              {
                forwardingTargetConfig: data.forwardingConfig,
                forwardingType: data.forwardingType
              }
            ),
          {
            successMessage: t('messages.forwarding.trigger.success'),
            errorMessage: t('messages.forwarding.trigger.error')
          }
        ),
      onSuccess: (_, variables) => {
        const { caseId, submissionId } = variables;
        queryClient.invalidateQueries({
          queryKey: [BehApiRoutes.caseWeiterleitung(caseId, submissionId)]
        });
      }
    });
  const submissionData =
    caseDetails && caseDetails?.caseId !== ''
      ? caseDetailsMapper(caseDetails)
      : null;

  const fachdaten = submissionData?.fachdaten ?? new FachdatenNode(undefined);

  const attachmentRequestData = extractAttachmentRequestData(
    caseDetails?.initialSubmission
  );

  function processIterationAndFiltering(fachdaten: FachdatenNode): void {
    if (fachdaten.children.length === 0) {
      return;
    }
    fachdaten.children.forEach((child): void => {
      filterChildrenByValue(child);
      processIterationAndFiltering(child);
    });

    filterChildrenByValue(fachdaten);
  }

  function filterChildrenByValue(fachdaten: FachdatenNode): void {
    fachdaten.children = fachdaten.children.filter((currentNode) => {
      currentNode.children = currentNode.children.filter((child) => {
        return child.labelValue?.value !== '- Keine Angabe -';
      });

      return (
        currentNode.children.length > 0 ||
        currentNode.labelValue?.label !== 'label'
      );
    });
  }

  if (isLoadingCaseDetails) {
    return (
      <div style={{ width: '50%' }}>
        <DefaultPageSkeleton />
      </div>
    );
  }
  if (!caseDetails || !caseDetails?.initialSubmission) {
    return <p> Es sind keine Daten vorhanden.</p>;
  }

  if (antragdetailsSettings.hideEmpty) {
    processIterationAndFiltering(fachdaten);
  }

  return (
    <>
      {isDisplaySettingsModalOpen && (
        <AntragsdetailsSettingsModal
          currentSettings={antragdetailsSettings}
          onSave={(settings) => {
            setAntragdetailsSettings(settings);
            setIsDisplaySettingsModalOpen(false);
          }}
          isOpen
          onClose={() => setIsDisplaySettingsModalOpen(false)}
        />
      )}
      {caseDetails ? (
        <div className={classes.horizontal}>
          {/*<div className={classes.leftbox}>*/}
          <Card elevation className={classes.leftbox}>
            <div>
              <h2 style={{ marginBottom: '10px' }}>Antragsdaten</h2>
              <br></br>
              <div className={classes.formularansicht}>
                <Button
                  variant="primary-text"
                  onClick={() => setIsDisplaySettingsModalOpen(() => true)}
                  beforeIcon={'settings-adjust'}
                  size={'sm'}
                >
                  {t('antragsdetails.displaySettings.settingsButton')}
                </Button>
                <Spacer />
                {attachmentRequestData && (
                  <Button
                    variant="primary-text"
                    beforeIcon={'view'}
                    size={'sm'}
                    onClick={handlePrint}
                    id={'formularansicht_display'}
                  >
                    Formularansicht
                  </Button>
                )}
                {!attachmentRequestData && (
                  <div className={classes.noFormularAnsicht}></div>
                )}
              </div>
            </div>
            {caseDetails.initialSubmission?.fachdatenV2 ? (
              <FachdatenV2
                data={caseDetails.initialSubmission.fachdatenV2}
                hideEmpty={antragdetailsSettings.hideEmpty}
                showApplicantLabels={antragdetailsSettings.showApplicantLabel}
              />
            ) : (
              <Stack direction="row" spacing="s">
                <Icon name="warning-alt-filled" color="warning-500" size="md" />
                <Text>{t('antragsdetails.fachdaten.cannotDisplay')}</Text>
              </Stack>
            )}
          </Card>
          {/*</div>*/}
          <div className={classes.rightbox}>
            <div className={classes.sticky}>
              <div className={classes.scroll}>
                <Stack direction="column" spacing="xs">
                  <VermerkData
                    caseDetails={caseDetails}
                    onStateChanged={() => refetchCaseDetails()}
                  />
                  <Metadaten caseDetails={caseDetails} />
                  <Ausweisdaten caseDetailsModel={caseDetails} />
                  <Accordion
                    label={
                      <h2 style={{ marginTop: '3px', marginLeft: '10px' }}>
                        {t('antragsdetails.weiterleitung.heading')}
                        {(!caseAttempts || caseAttempts.length === 0) && (
                          <span
                            style={{
                              marginLeft: '0.4rem',
                              fontWeight: 'normal'
                            }}
                          >
                            {t(
                              'antragsdetails.weiterleitung.nothingFoundHeading'
                            )}
                          </span>
                        )}
                      </h2>
                    }
                  >
                    <div style={{ marginLeft: '10px', marginRight: '10px' }}>
                      <div className={classes.collapsibleCardBody}>
                        {caseAttempts &&
                          !isLoading &&
                          caseAttempts.length === 0 &&
                          t('antragsdetails.weiterleitung.noFound')}
                        {isLoading && (
                          <DescriptionList>
                            {['1', '2', '3', '4'].map((item) => (
                              <DescriptionList.Item
                                key={item}
                                title={<Skeleton width="100px" />}
                                description={<Skeleton width="200px" />}
                              />
                            ))}
                          </DescriptionList>
                        )}
                        {caseAttempts?.map((destination, caseIndex) => (
                          <React.Fragment
                            key={destination.targetDestination.name}
                          >
                            <DescriptionList>
                              <DescriptionList.Item
                                key={'destination-name'}
                                title={t('antragsdetails.weiterleitung.key1')}
                                description={t(
                                  'antragsdetails.weiterleitung.value1',
                                  {
                                    destinationId:
                                      destination.targetDestination.name ||
                                      destination.targetDestination
                                        .forwardingTargetConfig.destinationId
                                  }
                                )}
                              />
                              <DescriptionList.Item
                                key={'destination-id'}
                                title={t('antragsdetails.weiterleitung.key2')}
                                description={
                                  destination.targetDestination
                                    .forwardingTargetConfig.destinationId
                                }
                              />
                              <DescriptionList.Item
                                key={'trigger-forwarding'}
                                title={t('antragsdetails.weiterleitung.key3')}
                                description={
                                  isTriggeringNewAttempt ? (
                                    <Spinner />
                                  ) : (
                                    <Button
                                      beforeIcon="send"
                                      size="sm"
                                      variant="primary-text"
                                      disabled={
                                        isTriggeringNewAttempt ||
                                        isPendingAttempt(
                                          destination.attemptEvents
                                        ) ||
                                        hasSuccessfulAttempt(
                                          destination.attemptEvents
                                        )
                                      }
                                      onClick={() => {
                                        triggerNewAttempt({
                                          forwardingType: 'FITCONNECT',
                                          forwardingConfig:
                                            destination.targetDestination
                                              .forwardingTargetConfig,
                                          caseId: caseDetails.caseId,
                                          submissionId:
                                            caseDetails.initialSubmission
                                              .submissionId
                                        })
                                          .then(() => {
                                            fetchCaseForwarding().catch(
                                              () => {}
                                            );
                                          })
                                          .catch(() => {});
                                      }}
                                    >
                                      {t('antragsdetails.weiterleitung.value3')}
                                    </Button>
                                  )
                                }
                              />
                              <DescriptionList.Item
                                key={'attempt-status'}
                                title={t('antragsdetails.weiterleitung.key4')}
                                description={
                                  <AttemptAlert
                                    destination={destination}
                                    t={t}
                                  />
                                }
                              />
                            </DescriptionList>
                            {caseIndex < caseAttempts.length - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  </Accordion>
                </Stack>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <Spinner />
      )}
      {caseDetails?.initialSubmission?.fachdatenV2 && (
        <div style={{ display: 'none' }}>
          <AntragPrintV2
            ref={componentRef}
            data={caseDetails.initialSubmission.fachdatenV2}
            caseDetails={caseDetails}
            hideEmpty={antragdetailsSettings.hideEmpty}
          />
        </div>
      )}
      {caseDetails?.initialSubmission?.fachdatenV2 && (
        <div style={{ display: 'none' }}>
          <AntragPrintV2
            ref={componentRef}
            data={caseDetails.initialSubmission.fachdatenV2}
            caseDetails={caseDetails}
            hideEmpty={antragdetailsSettings.hideEmpty}
          />
        </div>
      )}
    </>
  );
};

interface AntragsdetailsSettingsModalProps {
  currentSettings: AntragdetailsSettings;
  onSave: (settings: AntragdetailsSettings) => void;
  isOpen: boolean;
  onClose: () => void;
}

const AntragsdetailsSettingsModal = ({
  onSave,
  currentSettings,
  isOpen,
  onClose
}: AntragsdetailsSettingsModalProps) => {
  const { t } = useTranslation();
  const [antragsdetailsSettings, setAntragsdetailsSettings] =
    useState<AntragdetailsSettings>(currentSettings);
  return (
    <Modal open={isOpen} onClose={onClose}>
      <Modal.Header id="AntragsdetailsSettingsModal-header" onClose={onClose}>
        {t('antragsdetails.displaySettings.header')}
      </Modal.Header>
      <Modal.Content>
        <Stack direction={'column'} spacing="xs">
          <div>
            <Switch
              id="hideEmpty"
              label={t('antragsdetails.displaySettings.hideEmptySwitch')}
              defaultChecked={currentSettings.hideEmpty}
              onChange={(e) =>
                setAntragsdetailsSettings({
                  ...antragsdetailsSettings,
                  hideEmpty: e
                })
              }
            />
          </div>
          <div>
            <Switch
              id="showApplicantLabels"
              label={t(
                'antragsdetails.displaySettings.showApplicantLabelsSwitch'
              )}
              defaultChecked={currentSettings.showApplicantLabel}
              onChange={(e) =>
                setAntragsdetailsSettings({
                  ...antragsdetailsSettings,
                  showApplicantLabel: e
                })
              }
            />
          </div>
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <Button variant="secondary-outline" onClick={onClose}>
          {t('antragsdetails.displaySettings.secondaryButtonLabel')}
        </Button>
        <Button
          variant="primary"
          onClick={() => onSave(antragsdetailsSettings)}
        >
          {t('antragsdetails.displaySettings.primaryButtonLabel')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default Antragsdetails;
